// src/ApiPanel/components/ApiPanelSidebar.jsx
//
// What was wrong, and what each fix addresses:
//
//   Brand scrolled away    The whole <aside> was the scroll container, so the
//                          logo scrolled off with the nav. The aside is now a
//                          flex column: brand pinned, <nav> is the only thing
//                          that scrolls.
//
//   Collapsed mode broke   At 76px the labels and chevrons were still in the
//                          DOM at full size, so they overflowed the rail. They
//                          are hidden in CSS now and the icons centre; the
//                          `title` attribute carries the label on hover.
//
//   No mobile behaviour    The sidebar sat in the flex row at all widths and
//                          ate a phone screen. Under 900px it is an off-canvas
//                          drawer with a backdrop, driven by the same button.
//
//   Deep links looked lost With ~50 items, opening /reports/transactions/
//                          fino-cms left the active item somewhere far below
//                          the fold. The active link is scrolled into view.
//
//   Groups sprang open     The auto-expand effect merged on every route change,
//                          re-opening groups the user had deliberately closed.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { API_PANEL_NAV } from "../constants/apiPanelNav";

/** True if this item, or any of its descendants, matches the current path. */
function containsActivePath(item, pathname) {
  if (item.path && pathname === item.path) return true;
  if (item.path && pathname.startsWith(`${item.path}/`)) return true;
  return (item.children || []).some((child) => containsActivePath(child, pathname));
}

function NavGroup({ item, depth, openKeys, onToggle, pathname, collapsed, onNavigate, onExpandRail }) {
  const hasChildren = !!item.children?.length;
  const isOpen = openKeys.has(item.key);
  const isActive = containsActivePath(item, pathname);
  // Only indent inside an expanded rail — at 76px there is no room for it.
  const pad = collapsed ? 12 : 12 + depth * 14;

  if (!hasChildren) {
    return (
      <NavLink
        to={item.path}
        title={item.label}
        onClick={onNavigate}
        className={({ isActive: active }) => `apip-nav-link${active ? " active" : ""}`}
        style={{ paddingLeft: pad }}
      >
        {item.icon ? <i className={item.icon} /> : <span className="apip-nav-dot" />}
        <span className="apip-nav-label">{item.label}</span>
        {/* Shown only in the collapsed rail. The icons are a webfont, and if
            that font fails to load the rail would otherwise be a column of
            blank squares with no way to tell the items apart. */}
        <span className="apip-nav-mini">{item.label}</span>
      </NavLink>
    );
  }

  return (
    <div className="apip-nav-branch">
      <button
        type="button"
        className={`apip-nav-link apip-nav-toggle${isActive ? " active" : ""}`}
        style={{ paddingLeft: pad }}
        onClick={() => {
          // In the collapsed rail the children are hidden, so toggling would
          // be an invisible no-op. Widen the rail first, then open the group.
          if (collapsed) onExpandRail?.();
          onToggle(item.key);
        }}
        aria-expanded={isOpen}
        title={item.label}
      >
        {item.icon ? <i className={item.icon} /> : <span className="apip-nav-dot" />}
        <span className="apip-nav-label">{item.label}</span>
        <span className="apip-nav-mini">{item.label}</span>
        <i className={`iconoir-nav-arrow-down apip-nav-chevron${isOpen ? " open" : ""}`} />
      </button>
      {isOpen && !collapsed && (
        <div className="apip-nav-children">
          {item.children.map((child) => (
            <NavGroup
              key={child.key}
              item={child}
              depth={depth + 1}
              openKeys={openKeys}
              onToggle={onToggle}
              pathname={pathname}
              collapsed={collapsed}
              onNavigate={onNavigate}
              onExpandRail={onExpandRail}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ApiPanelSidebar({ collapsed, mobileOpen, onClose, onExpandRail }) {
  const { pathname } = useLocation();
  const navRef = useRef(null);

  const initialOpen = useMemo(() => {
    const keys = new Set();
    const walk = (items) => {
      for (const item of items) {
        if (item.children?.length && containsActivePath(item, pathname)) {
          keys.add(item.key);
          walk(item.children);
        }
      }
    };
    walk(API_PANEL_NAV);
    return keys;
  }, [pathname]);

  const [openKeys, setOpenKeys] = useState(initialOpen);

  // Auto-open the group containing the current route, but only when the route
  // moves into a group that is not already open. Merging on every pathname
  // change re-opened groups the user had deliberately collapsed.
  const lastAutoOpen = useRef("");
  useEffect(() => {
    const signature = [...initialOpen].sort().join("|");
    if (signature && signature !== lastAutoOpen.current) {
      lastAutoOpen.current = signature;
      setOpenKeys((prev) => new Set([...prev, ...initialOpen]));
    }
  }, [initialOpen]);

  // Bring the active link into view after the group expands. Deferred a frame
  // so the newly rendered children are measurable; `nearest` so it does not
  // yank an already-visible item to the middle.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const active = navRef.current?.querySelector(".apip-nav-link.active");
      active?.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(id);
  }, [pathname, openKeys]);

  // Escape closes the mobile drawer, and body scroll is locked while it is
  // open so the page behind does not scroll under the user's finger.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [mobileOpen, onClose]);

  const toggle = (key) =>
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <>
      {mobileOpen && <div className="apip-sidebar-backdrop" onClick={onClose} />}
      <aside
        className={
          `apip-sidebar${collapsed ? " apip-sidebar-collapsed" : ""}` +
          `${mobileOpen ? " apip-sidebar-open" : ""}`
        }
      >
        <div className="apip-sidebar-brand">
          <span className="apip-brand-mark">API</span>
          <span className="apip-brand-name">Panel</span>
          <button
            type="button"
            className="apip-icon-btn apip-sidebar-close"
            onClick={onClose}
            aria-label="Close navigation"
          >
            <i className="iconoir-xmark" />
          </button>
        </div>

        <nav className="apip-nav" ref={navRef} aria-label="API Panel navigation">
          {API_PANEL_NAV.map((item) => (
            <NavGroup
              key={item.key}
              item={item}
              depth={0}
              openKeys={collapsed ? new Set() : openKeys}
              onToggle={toggle}
              pathname={pathname}
              collapsed={collapsed}
              // Tapping a link on mobile should close the drawer; on desktop
              // onClose is a no-op because the drawer is not open.
              onNavigate={mobileOpen ? onClose : undefined}
              onExpandRail={onExpandRail}
            />
          ))}
        </nav>
      </aside>
    </>
  );
}
